import { FunctionComponent, useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { useAtom } from 'jotai';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next'

import { fetch_all, fetch_one } from '../../lib/server_helper';
import { loginAtom } from '../../lib/auth';
import { checkAllowed } from '../../lib/permission';

import {
  CircularProgress,
  Grid
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import GetAppIcon from '@mui/icons-material/GetApp';

import { AlertSomethingWentWrong } from '../../components/alerts/SomethingWentWrong';

import {
  DataGrid,
  Page,
  SearchBox,
  StyledButton,
  StyledIconButton,
  StyledTextField,
  Typography
} from '../../components/interface';

import { Iteration } from '../../models/Iteration';

// const { DateTime } = require("luxon");

type Props = {};

export const AdminIterations: FunctionComponent<Props> = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const {t, i18n} = useTranslation(['translations']);

  const [login, setLogin] = useAtom(loginAtom);

  const params = useParams();
  const projectId: string | undefined = params.id;

  const [iterations, setIterations] = useState<Iteration[]>([]);
  const [templatesCount, setTemplatesCount] = useState<number>(0);
  const [loader, setLoader] = useState<boolean>(true);
  const [editing, setEditing] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [order, setOrder] = useState<string>("id DESC");
  const [name, setName] = useState<string>("");
  const [activeId, setActiveId] = useState<number>();
  const [serverErrorSnackOpen, setServerErrorSnackOpen] = useState<boolean>(false);

  // Settings


  useEffect(() => {
    onSearch('');
  }, [page, order, projectId]);

  const onSearch = (s: string) => {
    if (!!projectId && checkAllowed(login, null, null, "Projects", 'r', true)) {
      // console.log(filters);
      setLoader(true);
      fetch_all<Iteration>('/nl/v3/objects/fetch_all', {
        object: 'iteration',
        fields: ['id', 'created_at', 'updated_at', 'name', 'extra_data', 'enabled', 'survey_count'],
        per_page: 20,
        page: page,
        order: order,
        filter: {
          search: s,
          advanced: {
            project_id: projectId,
            fallback: s
          }
        }
      }, (obs, complete_data) => {
        setIterations(obs);

        let count:number = 0;
        if (typeof(complete_data?.data) === 'object' && 'count' in complete_data?.data) count = complete_data?.data?.count;
        setTemplatesCount(count);

        setLoader(false);
      }, (z) => {
        setServerErrorSnackOpen(true);
      }, (!!login?.auth_token ? {auth_token: login?.auth_token, email: login?.email} : undefined));
    }
  };

  const saveSetting = () => {
    fetch_one<Iteration>('/nl/v3/objects/save', {
      object: 'iteration',
      fields: ['id'],
      ob: {
        project_id: projectId,
        name: name,
        ...(!!activeId ? {id: activeId} : {})
      },
      ...(!!activeId ? {id: activeId} : {})
    }, (ob, complete_data) => {
      onSearch('');
      setName('');
      setEditing(false);
    }, (z) => {
      setServerErrorSnackOpen(true);
    }, (!!login?.auth_token ? {auth_token: login?.auth_token, email: login?.email} : undefined));
  };

  const makeActive = (id:number) => {
    if (!!projectId && !!login && checkAllowed(login, null, null, "Projects", 'r', true)) {
      fetch_one<Iteration>('/nl/v3/objects/custom_action', {
        object: 'iteration',
        class_action: 'custom_api_promote',
        project_id: projectId,
        id: id
      }, (obs, complete_data) => {
        onSearch('');
      }, (z) => {

      }, login);
    }
  };





  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 100, sortable: true },
    { field: 'name', headerName: 'Name', width: 200, sortable: true },
    { field: 'enabled', headerName: 'Is Active', width: 120, sortable: false, renderCell: params => <>
      {!!params.row.enabled && <CheckIcon sx={{color: theme.palette.tertiary?.main, marginRight: 0.5}} />}
      {!params.row.enabled && <CloseIcon sx={{color: theme.palette.secondary.main, marginRight: 0.5}} />}
    </> },
    { field: 'survey_count', headerName: '# surveys', width: 120, sortable: true },
  ];

  return (
    <Page sx={{}} title={t("objects.iterations.title")} actions={<>
      {checkAllowed(login, null, null, "Projects", 'c', true) && <StyledButton
        label="Create new"
        id='create_new'
        contained
        onClick={() => {
          setEditing(true);
          setName('');
          setActiveId(undefined);
        }}
        sx={{marginRight: 3}}
        startIcon={<AddCircleOutlineIcon />}
      />}
    </>}>
      {!!editing && <>
        <Grid container spacing={1}>
          {!activeId && <Grid item xs={12}>
            <Typography>Create a new iteration for this project. It will not be enabled by default. You can pick a name, it can be changed afterwards.</Typography>
          </Grid>}
          <Grid item xs={12}>
            <StyledTextField
              label="Iteration name"
              value={name || ''}
              id="iteration_name"
              onChange={(v) => {setName(v);}}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            {<StyledButton
              label={t("public.buttons.cancel")}
              id='cancel'
              // contained
              onClick={(v) => {
                setEditing(false);
                setName('');
                setActiveId(undefined);
              }}
              sx={{marginLeft: 1}}
            />}
            {<StyledButton
              label={t("public.buttons.save")}
              id='save'
              contained
              onClick={(v) => {
                saveSetting();
              }}
              sx={{marginLeft: 1}}
            />}
          </Grid>
        </Grid>
      </>}

      {!editing && <DataGrid
        action_count={2}
        actions={(params) => {
          return (<>
            {!params.row.enabled && checkAllowed(login, params.row.company_id?.toString() || '0', params.row.id.toString(), "Projects", 'u') && <StyledIconButton title="Make active iteration" color="primary" onClick={() => {
              makeActive(params.row.id);
            }}>
              <GetAppIcon sx={{transform: 'rotate(180deg)'}} />
            </StyledIconButton>}
            {checkAllowed(login, params.row.company_id?.toString() || '0', params.row.id.toString(), "Projects", 'u') && <StyledIconButton title="Edit" color="primary" onClick={() => {
              setEditing(true);
              setName(params.row.name || '');
              setActiveId(params.row.id);
            }}>
              <EditIcon />
            </StyledIconButton>}
          </>);
        }}
        getRowClassName={(params) => `datagrid-row-enabled-${params.row.enabled}`}
        objectCount={templatesCount}
        objects={(iterations || []).map(u => ({
          ...u
        }))}
        columns={columns}
        loader={loader}
        onSortModelChange={(a, b) => {
          let a_0_field:string = a[0].field;
          setOrder([a_0_field, a[0].sort].join(' '));
        }}
        page={page}
        setPage={(p) => {setPage(p);}}
      />}

      {!!serverErrorSnackOpen && <AlertSomethingWentWrong open={serverErrorSnackOpen} setOpen={(b) => {setServerErrorSnackOpen(b);}} />}
    </Page>
  );
}
